import { supabase } from './supabase'

/**
 * Cancel subscription - sets cancel_at_period_end to true
 * Subscription will remain active until the end of the current billing period
 */
export const cancelSubscription = async (organizationId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Get current subscription
    const { data: subscription, error: subError } = await supabase
      .from('organization_subscriptions')
      .select('*')
      .eq('organization_id', organizationId)
      .single()

    if (subError) throw subError
    if (!subscription) throw new Error('No active subscription found')

    // If subscription has Paystack subscription code, cancel via Paystack API
    if (subscription.payment_provider === 'paystack' && subscription.payment_provider_subscription_id) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
      const { data: { session } } = await supabase.auth.getSession()
      
      const response = await fetch(`${supabaseUrl}/functions/v1/paystack-payment/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({
          subscription_code: subscription.payment_provider_subscription_id,
          organization_id: organizationId,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to cancel subscription with Paystack')
      }
    }

    // Update subscription to cancel at period end
    const { error: updateError } = await supabase
      .from('organization_subscriptions')
      .update({
        cancel_at_period_end: true,
        status: 'cancelled',
        updated_at: new Date().toISOString(),
      })
      .eq('organization_id', organizationId)

    if (updateError) throw updateError

    // Log activity
    await supabase.rpc('log_platform_activity', {
      action_type_param: 'subscription_cancelled',
      target_org_id: organizationId,
      details_param: {
        cancelled_by: user.id,
        cancelled_at: new Date().toISOString(),
        period_end: subscription.current_period_end,
      },
    }).catch(err => {
      console.warn('Failed to log cancellation activity:', err)
    })

    return { success: true, periodEnd: subscription.current_period_end }
  } catch (error) {
    console.error('Error cancelling subscription:', error)
    throw error
  }
}

/**
 * Reactivate a cancelled subscription
 */
export const reactivateSubscription = async (organizationId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Get current subscription
    const { data: subscription, error: subError } = await supabase
      .from('organization_subscriptions')
      .select('*')
      .eq('organization_id', organizationId)
      .single()

    if (subError) throw subError
    if (!subscription) throw new Error('No subscription found')

    // If subscription has Paystack subscription code, reactivate via Paystack API
    if (subscription.payment_provider === 'paystack' && subscription.payment_provider_subscription_id) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
      const { data: { session } } = await supabase.auth.getSession()
      
      const response = await fetch(`${supabaseUrl}/functions/v1/paystack-payment/enable-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({
          subscription_code: subscription.payment_provider_subscription_id,
          organization_id: organizationId,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to reactivate subscription with Paystack')
      }
    }

    // Update subscription to remove cancellation
    const { error: updateError } = await supabase
      .from('organization_subscriptions')
      .update({
        cancel_at_period_end: false,
        status: 'active',
        updated_at: new Date().toISOString(),
      })
      .eq('organization_id', organizationId)

    if (updateError) throw updateError

    // Log activity
    await supabase.rpc('log_platform_activity', {
      action_type_param: 'subscription_reactivated',
      target_org_id: organizationId,
      details_param: {
        reactivated_by: user.id,
        reactivated_at: new Date().toISOString(),
      },
    }).catch(err => {
      console.warn('Failed to log reactivation activity:', err)
    })

    return { success: true }
  } catch (error) {
    console.error('Error reactivating subscription:', error)
    throw error
  }
}

/**
 * Get subscription cancellation status
 */
export const getSubscriptionStatus = async (organizationId) => {
  try {
    const { data: subscription, error } = await supabase
      .from('organization_subscriptions')
      .select('status, cancel_at_period_end, current_period_end, billing_cycle')
      .eq('organization_id', organizationId)
      .single()

    if (error) throw error

    return {
      isCancelled: subscription?.cancel_at_period_end || subscription?.status === 'cancelled',
      periodEnd: subscription?.current_period_end,
      billingCycle: subscription?.billing_cycle,
      status: subscription?.status,
    }
  } catch (error) {
    console.error('Error getting subscription status:', error)
    throw error
  }
}

